// Drive setup — a utility screen, not a daily page. It calls the deployed
// `GET /api/v1/drive/tree` endpoint and renders the raw shared-drive and
// folder IDs so an OWNER/DIRECTOR can read them off the screen and paste
// them into config elsewhere. Deliberately NOT in the primary nav
// (lib/nav.ts) and NOT wired into the snapshot load (lib/bootstrap.ts) —
// this is a one-off lookup, fetched on demand like Company/My Desk.
//
// Access: client-side gated on `session.value.me?.role` (the same source
// every other role check in this app uses), same as the server route this
// calls. A MANAGER or FRONT_DESK who reaches /drive-setup never fires the
// fetch at all — never mind renders IDs — they get a plain refusal (INV-9:
// named, not blank).
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { useTableSort } from '../lib/tableSort';
import { SortableTh } from '../components/SortableTh';
import { authedGet } from '../lib/apiClient';
import { FaultState, EmptyState } from '../components/FaultState';
import { OpenFolderLink, driveFolderUrlFromId } from '../components/OpenFolderLink';
import { session } from '../lib/session';
import { useResource } from '../lib/store';

const DRIVE_TREE_KEY = 'drive-tree';

export interface DriveFolder {
  id: string;
  name: string;
}

export interface DriveEntry {
  id: string;
  name: string;
  folders: DriveFolder[];
}

export interface DriveTreeResponse {
  configured: boolean;
  drives?: DriveEntry[];
  error?: string;
  reason?: string;
}

function canSeeDriveSetup(role: string | null | undefined): boolean {
  return role === 'OWNER' || role === 'DIRECTOR';
}

export function DriveSetupPage(): JSX.Element {
  const allowed = canSeeDriveSetup(session.value.me?.role);
  const resource = useResource<DriveTreeResponse>(
    allowed ? DRIVE_TREE_KEY : null,
    () => authedGet('/api/v1/drive/tree'),
  );

  if (!allowed) {
    return (
      <div class="main">
        <div class="page-header"><h1>Drive setup</h1></div>
        <FaultState name="drive_setup_not_available" detail="This account cannot open Drive setup — OWNER or DIRECTOR only." />
      </div>
    );
  }

  if (resource.status === 'loading') {
    return (
      <div class="main">
        <div class="page-header"><h1>Drive setup</h1></div>
        <EmptyState label="Loading drive tree…" />
      </div>
    );
  }

  if (resource.status === 'fault') {
    return (
      <div class="main">
        <div class="page-header"><h1>Drive setup</h1></div>
        <FaultState name="drive_tree_load_failed" detail={resource.error} />
      </div>
    );
  }

  const payload = resource.data;

  if (!payload.configured) {
    return (
      <div class="main">
        <div class="page-header"><h1>Drive setup</h1></div>
        <EmptyState label="Drive is not configured yet." />
      </div>
    );
  }

  if (payload.error === 'not_shared') {
    return (
      <div class="main">
        <div class="page-header"><h1>Drive setup</h1></div>
        <FaultState name="drive_not_shared" detail={payload.reason || 'The service account is not on this drive.'} />
      </div>
    );
  }

  const drives = payload.drives ?? [];

  return (
    <div class="main">
      <div class="page-header">
        <h1>Drive setup</h1>
        <CopyJsonButton payload={payload} />
      </div>

      {drives.length === 0 ? (
        <EmptyState label="No shared drives found." />
      ) : (
        drives.map((drive) => (
          <DriveCard key={drive.id} drive={drive} />
        ))
      )}
    </div>
  );
}

/** One shared drive's folders, sortable by name or id (owner, 2026-08-19).
 * Its own component because a hook cannot run inside a `.map()` callback. */
function DriveCard({ drive }: { drive: DriveEntry }): JSX.Element {
  const folderSort = useTableSort(drive.folders, {
    folder: (f) => f.name,
    id: (f) => f.id,
  });
  return (
          <section class="card" data-testid={`drive-${drive.id}`}>
            <div class="section-head">
              <div class="section-head-row">
                <h2>{drive.name}</h2>
                <div class="section-head-actions">
                  <OpenFolderLink url={driveFolderUrlFromId(drive.id)} label={drive.name} />
                </div>
              </div>
              <p><code>{drive.id}</code></p>
            </div>
            {drive.folders.length === 0 ? (
              <EmptyState label="No folders in this drive." />
            ) : (
              <div class="table-wrap">
                <table data-testid={`drive-folders-${drive.id}`}>
                  <thead>
                    <tr>
                      <SortableTh label="Folder" sortKey="folder" state={folderSort.state} onSort={folderSort.toggle} />
                      <SortableTh label="ID" sortKey="id" state={folderSort.state} onSort={folderSort.toggle} />
                    </tr>
                  </thead>
                  <tbody>
                    {folderSort.rows.map((folder) => (
                      <tr key={folder.id} data-testid={`drive-folder-${folder.id}`}>
                        <td>
                          {driveFolderUrlFromId(folder.id) ? (
                            <a
                              class="doc-link"
                              href={driveFolderUrlFromId(folder.id)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {folder.name}
                            </a>
                          ) : folder.name}
                        </td>
                        <td><code>{folder.id}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
  );
}

/**
 * Copies the whole payload as JSON. `navigator.clipboard.writeText` is not
 * available in every context (no permission granted, non-HTTPS, an older
 * browser) — when it rejects or throws, the JSON is shown in a `<textarea>`
 * instead so it can still be selected and copied by hand, rather than
 * failing silently (INV-9: a failed copy must say so, not pretend to work).
 */
function CopyJsonButton({ payload }: { payload: DriveTreeResponse }): JSX.Element {
  const [fallbackText, setFallbackText] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    const json = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setFallbackText(null);
      setCopied(true);
    } catch {
      setCopied(false);
      setFallbackText(json);
    }
  }

  return (
    <div>
      <button type="button" class="btn" onClick={() => void copy()}>
        {copied ? 'Copied' : 'Copy as JSON'}
      </button>
      {fallbackText !== null && (
        <div>
          <small class="bad" role="alert">Clipboard copy failed — select the text below and copy it manually.</small>
          <textarea
            aria-label="Drive tree JSON"
            data-testid="drive-json-fallback"
            readOnly
            value={fallbackText}
            style={{ width: '100%', height: '200px' }}
          />
        </div>
      )}
    </div>
  );
}
